import { useQuery } from "react-query";
import { generateProcess } from "api/process";
import { GENERATE_PROCESS } from "@constants/reactQueryKeys";

/**
 * Custom hook to trigger the process generation and handle the response.
 * This hook uses `useQuery` to call the `generateProcess` API and triggers
 * the provided `onSuccess` or `onError` callbacks based on the result.
 *
 * Generation starts a pipeline, so a failed request is not retried automatically and
 * `onError` runs once per failed request. Callers re-run it explicitly (e.g. a retry button).
 *
 * @category Hooks
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Flag to determine if the query should be enabled.
 * @param {Object} options.processData - The data required to generate the process. Includes targetProcess and platformContext.
 * @param {Function} options.onSuccess - Callback function triggered when the request succeeds.
 * @param {Function} options.onError - Callback function triggered when the request fails.
 * @returns {Object} Query result object containing:
 * - `generateError` {Error | null} - Error object if the request fails.
 * - `data` {Object | undefined} - The result data from the process generation.
 */

const useGenerateProcess = ({ enabled, processData, onSuccess, onError }) => {
  const { data, error } = useQuery(
    [GENERATE_PROCESS, processData],
    () => generateProcess(processData),
    {
      enabled: enabled && !!processData,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess,
      onError,
    }
  );

  return { generateError: error, data };
};

export default useGenerateProcess;
